import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FormikErrors } from "formik";
import { useCallback, useMemo, useState } from "react";

import DamageTypeAutocompleteV2 from "@/common/components/Entity/ReferenceData/DamageType/DamageTypeAutocompleteV2";
import VehiclePartTypeAutocompleteV2 from "@/common/components/Entity/ReferenceData/VehiclePartType/VehiclePartTypeAutocompleteV2";
import VehicleDamageLink from "@/common/components/Entity/VehicleDamage/VehicleDamageLink";
import ApiEnumAutocomplete from "@/common/components/Enum/ApiEnumAutocomplete";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { CloneHelper } from "@/common/helpers/clone";
import { VehiclePartTypeHelper } from "@/common/helpers/entity/vehiclePartType";
import { VisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { noop } from "@/common/helpers/function";
import { GenericDamageHelper } from "@/common/helpers/genericDamage";
import { StringHelper } from "@/common/helpers/string";
import { SvgHelper } from "@/common/helpers/svg";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";
import { Mapper } from "@/common/mapping/mapper";
import { ApiEnumName } from "@/common/services/enum";
import { EntityFormMode, EntityInputFormInfo } from "@/common/ts/entity";
import { GenericDamage } from "@/common/ts/visualModel";
import { ValidationHelper } from "@/common/validation";
import { VehicleArea, VehicleProjection, VehicleVisualModelDto } from "@/core/api/generated/v1";

import NoDataAlert from "../../../../../AppAlerts/NoDataAlert";
import AppButton from "../../../../../Button/AppButton";
import AppIconButton from "../../../../../Button/AppIconButton";
import AppCard from "../../../../../Card/AppCard";
import AppCardContent from "../../../../../Card/AppCardContent";
import FileListView from "../../../../../Files/FileListView";
import FileUploader from "../../../../../Files/FileUploader";
import AppTextArea from "../../../../../Form/Input/AppTextArea";
import AppIcon from "../../../../../Icons/AppIcon";
import CompoundIcon from "../../../../../Icons/CompoundIcon";
import MenuWithTrigger from "../../../../../Menu/MenuWithTrigger";
import ConfirmationModal, { ConfirmationModalProps } from "../../../../../Modals/ConfirmationModal";
import AppPopover from "../../../../../Popover/AppPopover";
import AppPopoverContent from "../../../../../Popover/AppPopoverContent";
import AppTypography from "../../../../../Text/AppTypography";
import { VisualModelSvgViewer } from "../VisualModelSvgViewer";
import { VisualModelViewer } from "../VisualModelViewer";
import { PartTypeExtraFilters } from "./VisualModelEditorOfGenericDamage";

interface Props<TDamage extends GenericDamage> extends ConfirmationModalProps {
  formMode: EntityFormMode;
  vehicleId: Nil<string>;
  visualModel: Nil<VehicleVisualModelDto>;
  damages: TDamage[];
  damage: TDamage;
  isUpdateEnabled: boolean;
  isDeleteEnabled: boolean;
  isSplitEnabled: boolean;
  isMergeEnabled: boolean;
  isPartTypeDisabled?: boolean;
  isDamageTypeDisabled?: boolean;
  isSubmitDisabled?: boolean;
  isPreload?: boolean;
  partTypeExtraFilters: PartTypeExtraFilters;
  errors: Nil<FormikErrors<TDamage>>;
  onCreated: (newValue: TDamage) => void;
  onUpdated: (newValue: TDamage) => void;
  onDeleted: (newValue: TDamage) => void;
  /** When N damages change (mix of created, updated and deleted).
   * E.g. on damage split or merge.
   * NB: Passes only changed damages, not all.
   */
  onManyChanged: (params: { added?: TDamage[]; updated?: TDamage[]; removed?: TDamage[] }) => void;
  setPartTypeExtraFilters: (newValue: PartTypeExtraFilters) => void;
}

export function VisualModelEditorOfGenericDamageInputModal<TDamage extends GenericDamage>({
  formMode,
  vehicleId,
  visualModel,
  damages,
  damage,
  isUpdateEnabled,
  isDeleteEnabled,
  isSplitEnabled,
  isMergeEnabled,
  isPartTypeDisabled,
  isDamageTypeDisabled,
  isSubmitDisabled,
  isPreload,
  partTypeExtraFilters,
  errors,
  onCreated,
  onUpdated,
  onDeleted,
  onManyChanged,
  setPartTypeExtraFilters,
  ...modalProps
}: Props<TDamage>) {
  const formInfo: EntityInputFormInfo = {
    entityId: undefined,
    mode: formMode,
    isCreate: formMode === EntityFormMode.Create,
    isUpdate: formMode === EntityFormMode.Update,
  };

  const damagesRef = useLatestRef(damages);

  const [damageLocal, setDamageLocal] = useState<TDamage>(damage);
  const [isNotesVisible, setIsNotesVisible] = useState(true);
  const [isAttachmentsVisible, setIsAttachmentsVisible] = useState(true);
  const [isAttachmentsUploading, setIsAttachmentsUploading] = useState(false);
  const [isAttachmentsHaveErrors, setIsAttachmentsHaveErrors] = useState(false);

  const [isSplitActive, setIsSplitActive] = useState(false);
  const [damagesToSplitFromSource, setDamagesToSplitFromSource] = useState<TDamage[] | null>(null);

  const [isMergeActive, setIsMergeActive] = useState(false);
  const [isMergeDamagesFromDifferentProjections, setIsMergeDamagesFromDifferentProjections] =
    useState(false);
  const [isMergePreview, setIsMergePreview] = useState(false);
  const [originalDamageForMergeTarget, setOriginalDamageForMergeTarget] = useState<TDamage | null>(
    null,
  ); // damage on which merge is initiated and from which merge target damage is created
  const [damagesToMergeIntoTarget, setDamagesToMergeIntoTarget] = useState<TDamage[] | null>(null);

  const point = useMemo(
    () => Mapper.visualModel.mapGenericDamageToVisualModelPoint<TDamage>(damageLocal),
    [damageLocal],
  );

  const resetDamageType = useCallback((newValue: TDamage): TDamage => {
    return GenericDamageHelper.resetDamageType(newValue);
  }, []);

  const resetPartType = useCallback((newValue: TDamage): TDamage => {
    return GenericDamageHelper.resetPartTypeAndDamageType(newValue);
  }, []);

  const createNewSplitTargetDamage = (): TDamage => {
    return Mapper.genericDamage.mapToNew(damageLocal);
  };

  const isAllSplitTargetDamagesValid = (): boolean => {
    return (
      ArrayHelper.isNotEmpty(damagesToSplitFromSource) &&
      damagesToSplitFromSource.length >= 2 &&
      damagesToSplitFromSource.every((x) => !!x.partTypeId && !!x.damageTypeId)
    );
  };

  const isAllMergeSourceDamagesValid = (): boolean => {
    return ArrayHelper.isNotEmpty(damagesToMergeIntoTarget) && damagesToMergeIntoTarget.length >= 2;
  };

  return (
    <ConfirmationModal
      {...modalProps}
      keepMounted={false}
      fullWidth
      maxWidth='md'
      open={modalProps.open}
      isAutoCloseOnCancel={false}
      isAutoCloseOnConfirm={false}
      isConfirmEnabled={
        !!damageLocal.partTypeId &&
        !!damageLocal.damageTypeId &&
        !!damageLocal.area &&
        (damageLocal.area === VehicleArea.Exterior
          ? !!damageLocal.projection && !!damageLocal.point
          : true) &&
        !isAttachmentsUploading &&
        !isAttachmentsHaveErrors &&
        !isSubmitDisabled &&
        (formInfo.isCreate || (formInfo.isUpdate && isUpdateEnabled))
      }
      onClose={() => {
        modalProps.onClose && modalProps.onClose({}, "escapeKeyDown");

        setPartTypeExtraFilters(undefined);

        setIsSplitActive(false);
        setDamagesToSplitFromSource(null);

        setIsMergeActive(false);
        setIsMergePreview(false);
        setDamagesToMergeIntoTarget(null);
        setOriginalDamageForMergeTarget(null);
      }}
      onCancel={(params) => {
        if (isSplitActive) {
          //cancel split
          setIsSplitActive(false);
          setDamagesToSplitFromSource(null);
        } else if (isMergeActive) {
          //cancel merge
          if (originalDamageForMergeTarget) {
            setDamageLocal(originalDamageForMergeTarget);
          }
          setIsMergeActive(false);
          setDamagesToMergeIntoTarget(null);
          setOriginalDamageForMergeTarget(null);
        } else {
          // cancel update
          params.closeModal();
        }
      }}
      onConfirm={(params) => {
        if (!damageLocal) {
          return;
        }

        // create
        if (formInfo.isCreate) {
          onCreated(CloneHelper.cloneDeep(damageLocal));
          params.closeModal();
          return;
        }

        // update
        const isSplitOrMerge = isSplitActive || isMergeActive || isMergePreview;
        const isUpdateOnly = !isSplitOrMerge;
        if (formInfo.isUpdate && isUpdateOnly) {
          onUpdated(CloneHelper.cloneDeep(damageLocal));
          params.closeModal();
          return;
        }

        // update + split, merge
        if (formInfo.isUpdate && isSplitOrMerge) {
          if (isSplitActive) {
            // perform split and close the modal
            onManyChanged({
              added: damagesToSplitFromSource
                ? [...damagesToSplitFromSource.map((x) => ({ ...x, originalId: damageLocal.id }))]
                : undefined, // add target damage
              removed: [damageLocal], // remove source damage
            });
            params.closeModal();
          } else if (isMergeActive) {
            // preview merge (make local changes only) and keep the modal open

            // ensure merge target damage is new, created from N source damages
            const mergeTarget = {
              ...damageLocal,
              originalId: originalDamageForMergeTarget?.id,
              originalIds: ArrayHelper.undefinedIfEmpty(damagesToMergeIntoTarget?.map((x) => x.id)),
            };

            // concat notes
            mergeTarget.notes = StringHelper.join(
              damagesToMergeIntoTarget?.map((x) => x.notes) || [],
              "\n",
              { skipEmpty: true },
            );

            // concat attachments
            const newAttachments =
              damagesToMergeIntoTarget?.flatMap((x) => x.attachments ?? []) || [];
            mergeTarget.attachments = [...newAttachments];

            setDamageLocal(mergeTarget);
            setIsMergeActive(false);
            setIsMergePreview(true);
          } else {
            if (isMergePreview) {
              // perform merge: remove source damages, update target damage
              onManyChanged({
                added: [CloneHelper.cloneDeep(damageLocal)],
                removed: damagesToMergeIntoTarget ? [...damagesToMergeIntoTarget] : undefined,
              });
            } else {
              // update damage
              onManyChanged({
                updated: [CloneHelper.cloneDeep(damageLocal)],
              });
            }

            params.closeModal();
          }
        }
      }}
      cancelButtonProps={{
        children: isSplitActive
          ? "Cancel split"
          : isMergeActive || isMergePreview
            ? "Cancel merge"
            : "Cancel",
      }}
      confirmButtonProps={{
        disabled: isSplitActive
          ? !isAllSplitTargetDamagesValid()
          : isMergeActive
            ? !isAllMergeSourceDamagesValid()
            : undefined,
        startIcon: isSplitActive ? (
          <AppIcon of='split' />
        ) : isMergeActive ? (
          <AppIcon of='preview' />
        ) : isMergePreview ? (
          <AppIcon of='merge' />
        ) : undefined,
        children: isSplitActive
          ? "Perform split"
          : isMergeActive
            ? "Preview merge"
            : isMergePreview
              ? "Perform merge"
              : "Save",
      }}
      title={
        <Box>
          {formInfo.isCreate ? "Add damage" : "Edit damage"}

          {/* TODO: This is DD specific. Should be passed as customization. */}
          {formInfo.isUpdate && damageLocal.vehicleDamage && (
            <AppPopover
              hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
              trigger={
                <AppIconButton size='small' color='info'>
                  <AppIcon of='info' />
                </AppIconButton>
              }
            >
              <AppPopoverContent isUseDefaultStyles={false} isTransparent>
                <Alert severity='info' sx={{ maxWidth: { xxs: "90vw", md: 400 } }}>
                  The corresponding damage{" "}
                  <VehicleDamageLink
                    vehicleId={damageLocal.vehicleDamage?.vehicle?.id}
                    entity={damageLocal.vehicleDamage}
                    entityId={undefined}
                  />{" "}
                  will be auto-updated with the new details in the vehicle history after submitting
                  the form.
                </Alert>
              </AppPopoverContent>
            </AppPopover>
          )}
        </Box>
      }
      body={
        <Box sx={{ pt: 1 }}>
          <Stack gap={2}>
            {/* Alerts */}
            {!isSplitActive &&
              !isMergeActive &&
              (() => {
                const isAreaNotSet = !damageLocal.area;
                const isProjectionNoSet =
                  damageLocal.area === VehicleArea.Exterior &&
                  !damageLocal.projection &&
                  !!damageLocal.point;
                const isPointNotSet =
                  damageLocal.area === VehicleArea.Exterior && !damageLocal.point;

                return isAreaNotSet || isProjectionNoSet || isPointNotSet ? (
                  <Alert severity='error'>
                    {isAreaNotSet && <Box>{`Area isn't specified. It's required.`}</Box>}

                    {isProjectionNoSet && (
                      <Box>{`Projection isn't specified. It's required for exterior damages to correctly display damage point.`}</Box>
                    )}

                    {isPointNotSet && (
                      <Box>
                        {`Damage point isn't specified. It's required for exterior damages.`}
                      </Box>
                    )}
                  </Alert>
                ) : null;
              })()}

            {/* Split damages */}
            {isSplitActive && (
              <>
                <Alert severity='info'>
                  <AlertTitle>How to split damage:</AlertTitle>

                  <Box component='ol' sx={{ m: 0 }}>
                    <li>
                      Add multiple new target damages that the source damage will be split on.
                    </li>
                    <li>
                      Click <strong>Preview split</strong> and review the split or{" "}
                      <strong>Cancel</strong> to abort the split.
                    </li>
                    <li>
                      Click <strong>Save</strong> to perform the split.
                    </li>
                    <li>
                      As a result, the source damage is deleted and the target damages are created.
                    </li>
                  </Box>
                </Alert>

                <AppTypography variant='h3'>Split source damage</AppTypography>
              </>
            )}

            {/* Merge damages */}
            {isMergeActive && (
              <>
                <Alert severity='info'>
                  <AlertTitle>How to merge damages:</AlertTitle>

                  <Box component='ol' sx={{ m: 0 }}>
                    <li>
                      Select multiple source damages that need to be merged into target damage.
                    </li>
                    <li>
                      Click <strong>Preview merge</strong> and review the merge or{" "}
                      <strong>Cancel</strong> to abort the merge.
                    </li>
                    <li>
                      Click <strong>Save</strong> to perform the merge.
                    </li>
                    <li>
                      As a result, the source damages are deleted and the target damage is created.
                    </li>
                  </Box>
                </Alert>
              </>
            )}

            {isMergeActive && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <AppTypography variant='h3'>Merge source damages</AppTypography>

                <Stack spacing={2}>
                  <Autocomplete<TDamage, true>
                    multiple
                    disableCloseOnSelect
                    options={
                      damagesRef.current?.filter(
                        (x) =>
                          x.id !== damageLocal.id &&
                          (isMergeDamagesFromDifferentProjections ||
                            x.projection === damageLocal.projection),
                      ) || []
                    }
                    getOptionLabel={(option) =>
                      `${option.partType?.name || option.partTypeRef?.name || ""} - ${option.damageType?.name || option.damageTypeRef?.name || ""}`
                    }
                    value={damagesToMergeIntoTarget || []}
                    onChange={(e, newValue) => {
                      setDamagesToMergeIntoTarget(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        variant='outlined'
                        label='Source damages to merge into the target damage'
                        placeholder='Select 2 or many'
                      />
                    )}
                    renderOption={(props, option) => (
                      <ListItem {...props} key={option.id || props.key}>
                        <ListItemIcon>
                          <AppIcon of='damage' />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant='body1' component='div'>
                              {`${option.partType?.name || option.partTypeRef?.name || ""} - ${option.damageType?.name || option.damageTypeRef?.name || ""}`}
                            </Typography>
                          }
                          secondary={
                            <Typography component='div' variant='body2'>
                              {option.notes && <Box>{option.notes}</Box>}

                              <FileListView
                                files={FileItem.createManyFrom(option.attachments)}
                                maxVisibleFiles={10}
                                itemHeight={50}
                                isShowNoFilesAlert={false}
                              />
                            </Typography>
                          }
                        />
                      </ListItem>
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='isEnabledMergeDifferentProjection'
                        checked={isMergeDamagesFromDifferentProjections}
                        onChange={(newValue) =>
                          setIsMergeDamagesFromDifferentProjections(newValue.target.checked)
                        }
                      />
                    }
                    label='Enable to merge damages from all projections'
                  />
                </Stack>

                <Divider />
              </Stack>
            )}

            {isMergeActive && <AppTypography variant='h3'>Merge target damage</AppTypography>}

            {/* Damage editor (move only for current damage) */}
            {!isSplitActive && !isMergeActive && (
              <VisualModelViewer
                variant='compact'
                direction='row'
                visualModelId={undefined}
                visualModel={visualModel}
                points={point ? [point] : []}
                initialMainImageSelector={{
                  area: damageLocal.area || VehicleArea.Exterior,
                  projection: damageLocal.projection || VehicleProjection.Front,
                }}
                isFetchVisualModel={false}
                mainViewerProps={{
                  svgViewerProps: {
                    svgProps: {
                      width: 450,
                      height: 450,
                      minWidth: 300,
                      minHeight: 300,
                      maxWidth: 500,
                      maxHeight: 500,
                      cursor: isUpdateEnabled ? "crosshair" : "default",
                      point: {
                        cursor: isUpdateEnabled ? "pointer" : "default",
                      },
                    },
                    isPointDragEnabled: isUpdateEnabled,
                  },
                  onNewPointClicked: (params) => {
                    setDamageLocal((prev) => {
                      let newDamage: TDamage = {
                        ...prev,
                        visualModelId: visualModel?.id,
                        area: params.area,
                        projection: params.projection,
                        point: SvgHelper.mapSvgPointFullCoordsToGeneralSvgPointDto(params.coords),
                        svgPathMetadata: params.svgPathMetadata,
                        isRepositioningRequired: false,
                      };
                      if (
                        prev.partType &&
                        ((params.area &&
                          !VehiclePartTypeHelper.hasArea(prev.partType, params.area)) ||
                          (params.projection &&
                            !VehiclePartTypeHelper.hasProjection(prev.partType, params.projection)))
                      ) {
                        newDamage = resetPartType(newDamage);
                      }
                      return newDamage;
                    });

                    setPartTypeExtraFilters(
                      params.svgPathMetadata
                        ? {
                            damageId: damageLocal.id,
                            category: params.svgPathMetadata?.vehiclePartCategory || undefined,
                            types: params.svgPathMetadata?.vehiclePartTypes || undefined,
                            descriptors:
                              params.svgPathMetadata?.vehiclePartDescriptors || undefined,
                          }
                        : undefined,
                    );
                  },
                  onPointClicked: noop,
                  onPointMoved: (params) => {
                    setDamageLocal((prev) => ({
                      ...prev,
                      visualModelId: visualModel?.id,
                      point: SvgHelper.mapSvgPointFullCoordsToGeneralSvgPointDto(params.endCoords),
                      svgPathMetadata: params.svgPathMetadata,
                      isRepositioningRequired: false,
                    }));

                    setPartTypeExtraFilters(
                      params.svgPathMetadata
                        ? {
                            damageId: damageLocal.id,
                            category: params.svgPathMetadata?.vehiclePartCategory || undefined,
                            types: params.svgPathMetadata?.vehiclePartTypes || undefined,
                            descriptors:
                              params.svgPathMetadata?.vehiclePartDescriptors || undefined,
                          }
                        : undefined,
                    );
                  },
                }}
                secondaryViewerProps={{}}
              />
            )}

            <Box>
              <Grid container rowSpacing={1} columnSpacing={1}>
                <Grid size={{ xxs: 12, md: 5 }}>
                  <FormControl margin='none' fullWidth>
                    <VehiclePartTypeAutocompleteV2
                      entityId={damageLocal.partTypeId}
                      isPreload={isPreload}
                      isAutoSelectSingleOption={formInfo.isCreate}
                      isAutoSelectFirstOption={formInfo.isCreate && !!partTypeExtraFilters}
                      size='small'
                      required
                      disabled={!isUpdateEnabled || isPartTypeDisabled}
                      metadata={{
                        area: damageLocal.area,
                        projection: damageLocal.projection,
                      }}
                      searchFilters={{
                        area: damageLocal.area,
                        projection: damageLocal.projection,
                        ...(partTypeExtraFilters?.damageId === damageLocal.id
                          ? {
                              category: partTypeExtraFilters?.category,
                              types: partTypeExtraFilters?.types,
                              descriptors: partTypeExtraFilters?.descriptors,
                            }
                          : {
                              category: damageLocal.partType?.category,
                              type: damageLocal.partType?.type || undefined,
                              descriptor: damageLocal.partType?.descriptor || undefined,
                            }),
                        isEnabledForDamageDetection: true,
                      }}
                      textFieldProps={{
                        error: Boolean(errors?.partTypeId),
                        helperText: ValidationHelper.getFormikErrorsAsString(errors?.partTypeId),
                      }}
                      onChange={async (newValue) => {
                        setDamageLocal((prev) => {
                          let newDamage: TDamage = {
                            ...prev,
                            partTypeId: newValue?.id,
                            partType: newValue,
                            partTypeRef: undefined,
                          };
                          newDamage = resetDamageType(newDamage);
                          return newDamage;
                        });
                      }}
                      onPaginatedLoaded={(paginated) => {
                        if (paginated?.pagination?.totalCount === 0) {
                          setPartTypeExtraFilters(undefined);
                        }
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xxs: 12, md: 5 }}>
                  <FormControl margin='none' fullWidth>
                    <DamageTypeAutocompleteV2
                      entityId={damageLocal.damageTypeId}
                      isPreload={isPreload && !!damageLocal.partTypeId}
                      // isAutoSelectSingleOption={formInfo.isCreate && !!damageLocal.partTypeId}
                      // isAutoSelectFirstOption={formInfo.isCreate && !!damageLocal.partTypeId}
                      isAutoSelectSingleOption={!!damageLocal.partTypeId}
                      isAutoSelectFirstOption={!!damageLocal.partTypeId}
                      size='small'
                      required
                      disabled={!isUpdateEnabled || isDamageTypeDisabled || !damageLocal.partTypeId}
                      searchFilters={{
                        vehiclePartTypeId: damageLocal.partTypeId || undefined,
                      }}
                      textFieldProps={{
                        error: Boolean(errors?.damageTypeId),
                        helperText: ValidationHelper.getFormikErrorsAsString(errors?.damageTypeId),
                      }}
                      onChange={async (newValue) => {
                        setDamageLocal((prev) => ({
                          ...prev,
                          damageTypeId: newValue?.id,
                          damageType: newValue,
                          damageTypeRef: undefined,
                        }));
                      }}
                    />
                  </FormControl>
                </Grid>

                {/* Controls */}
                <Grid size={{ xxs: 12, md: 2 }}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='flex-end'
                    spacing={0.5}
                    sx={{ width: "100%" }}
                  >
                    {!isNotesVisible && (
                      <Tooltip title='Add notes'>
                        <AppIconButton
                          disabled={!isUpdateEnabled}
                          onClick={() => {
                            setIsNotesVisible(true);
                          }}
                        >
                          <CompoundIcon
                            mainIcon={<AppIcon of='notes' />}
                            badgeIcon={<AppIcon of='add' />}
                          />
                        </AppIconButton>
                      </Tooltip>
                    )}
                    {isNotesVisible && (
                      <Tooltip title='Remove notes'>
                        <AppIconButton
                          disabled={!isUpdateEnabled}
                          onClick={() => {
                            setIsNotesVisible(false);
                            setDamageLocal((prev) => ({
                              ...prev,
                              notes: null,
                            }));
                          }}
                        >
                          <CompoundIcon
                            mainIcon={<AppIcon of='notes' />}
                            badgeIcon={<AppIcon of='remove' />}
                          />
                        </AppIconButton>
                      </Tooltip>
                    )}

                    {!isAttachmentsVisible && (
                      <Tooltip title='Add attachments'>
                        <AppIconButton
                          disabled={!isUpdateEnabled}
                          onClick={() => {
                            setIsAttachmentsVisible(true);
                          }}
                        >
                          <CompoundIcon
                            mainIcon={<AppIcon of='attachments' />}
                            badgeIcon={<AppIcon of='add' />}
                          />
                        </AppIconButton>
                      </Tooltip>
                    )}
                    {isAttachmentsVisible && (
                      <Tooltip title='Remove attachments'>
                        <AppIconButton
                          disabled={!isUpdateEnabled}
                          onClick={() => {
                            setIsAttachmentsVisible(false);
                            setDamageLocal((prev) => ({
                              ...prev,
                              attachments: null,
                            }));
                          }}
                        >
                          <CompoundIcon
                            mainIcon={<AppIcon of='attachments' />}
                            badgeIcon={<AppIcon of='remove' />}
                          />
                        </AppIconButton>
                      </Tooltip>
                    )}

                    {/* Menu */}
                    {formInfo.isUpdate && (isSplitEnabled || isMergeEnabled || isDeleteEnabled) && (
                      <MenuWithTrigger
                        withAuthCloseOnClick
                        trigger={
                          <AppIconButton>
                            <AppIcon of='moreVert' />
                          </AppIconButton>
                        }
                      >
                        {formInfo.isUpdate &&
                          isSplitEnabled &&
                          !isSplitActive &&
                          !isMergeActive && (
                            <MenuItem
                              onClick={() => {
                                setDamagesToSplitFromSource([
                                  createNewSplitTargetDamage(),
                                  createNewSplitTargetDamage(),
                                ]);
                                setIsSplitActive(true);
                              }}
                            >
                              <ListItemIcon>
                                <AppIcon of='split' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Split damage</ListItemText>
                            </MenuItem>
                          )}

                        {formInfo.isUpdate &&
                          isMergeEnabled &&
                          !isSplitActive &&
                          !isMergeActive && (
                            <MenuItem
                              onClick={() => {
                                setOriginalDamageForMergeTarget(CloneHelper.cloneDeep(damageLocal));
                                setDamagesToMergeIntoTarget([damageLocal]);

                                // ensure merge target damage is new, created from N source damages
                                const mergeTarget: TDamage = Mapper.genericDamage.mapToNew(
                                  damageLocal,
                                  {
                                    originalId: undefined,
                                    originalIds: undefined,
                                  },
                                );
                                setDamageLocal(mergeTarget);

                                setIsMergeActive(true);
                              }}
                            >
                              <ListItemIcon>
                                <AppIcon of='merge' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Merge damages</ListItemText>
                            </MenuItem>
                          )}

                        {formInfo.isUpdate && isDeleteEnabled && (
                          <MenuItem
                            onClick={(e) => {
                              onDeleted(damageLocal);
                            }}
                          >
                            <ListItemIcon>
                              <AppIcon of='delete' fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>Delete damage</ListItemText>
                          </MenuItem>
                        )}
                      </MenuWithTrigger>
                    )}
                  </Stack>
                </Grid>
              </Grid>

              <Grid sx={{ mt: 2 }} container rowSpacing={1} columnSpacing={1}>
                <Grid size={{ xxs: 12, md: 5 }}>
                  <FormControl margin='none' fullWidth>
                    <ApiEnumAutocomplete
                      type={ApiEnumName.VehicleArea}
                      onlyEnumValues={[VehicleArea.Exterior, VehicleArea.Interior]}
                      value={damageLocal.area}
                      label='Area'
                      size='small'
                      required
                      disabled={!isUpdateEnabled}
                      textFieldProps={{
                        error: Boolean(errors?.area),
                        helperText: ValidationHelper.getFormikErrorsAsString(errors?.area),
                      }}
                      onChange={(newValue) => {
                        setDamageLocal((prev) => {
                          let newDamage: TDamage = {
                            ...prev,
                            area: newValue,
                            projection: null,
                            point: prev.area === newValue ? damageLocal.point : null,
                          };
                          if (
                            newValue &&
                            prev.partType &&
                            !VehiclePartTypeHelper.hasArea(prev.partType, newValue)
                          ) {
                            newDamage = resetPartType(newDamage);
                          }
                          return newDamage;
                        });
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xxs: 12, md: 5 }}>
                  <FormControl margin='none' fullWidth>
                    <ApiEnumAutocomplete
                      type={ApiEnumName.VehicleProjection}
                      onlyEnumValues={[
                        VehicleProjection.Front,
                        VehicleProjection.Back,
                        VehicleProjection.Left,
                        VehicleProjection.Right,
                        VehicleProjection.Top,
                      ]}
                      value={damageLocal.projection}
                      label='Projection'
                      size='small'
                      required={false}
                      disabled={!isUpdateEnabled || damageLocal.area !== VehicleArea.Exterior}
                      textFieldProps={{
                        error: Boolean(errors?.projection),
                        helperText: ValidationHelper.getFormikErrorsAsString(errors?.projection),
                      }}
                      onChange={(newValue) => {
                        setDamageLocal((prev) => {
                          let newDamage: TDamage = {
                            ...prev,
                            projection: newValue,
                            point: prev.projection === newValue ? damageLocal.point : null,
                          };
                          if (
                            newValue &&
                            prev.partType &&
                            !VehiclePartTypeHelper.hasProjection(prev.partType, newValue)
                          ) {
                            newDamage = resetPartType(newDamage);
                          }
                          return newDamage;
                        });
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {damageLocal && (isNotesVisible || isAttachmentsVisible) && (
              <Box>
                {isNotesVisible && (
                  <AppTextArea
                    fullWidth
                    mode='notes'
                    margin='none'
                    onChange={(e) => {
                      setDamageLocal((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }));
                    }}
                    type='text'
                    value={damageLocal.notes || ""}
                    disabled={!isUpdateEnabled}
                    variant='outlined'
                    error={Boolean(errors?.notes)}
                    helperText={ValidationHelper.getFormikErrorsAsString(errors?.notes)}
                  />
                )}

                {isAttachmentsVisible && (
                  <FormControl fullWidth margin='dense'>
                    <FileUploader
                      multiple
                      // defaultFiles={FileItem.createManyFrom(
                      //   damagesRef.current[
                      //     damagesRef.current.findIndex((el) => itemValuesLocal?.id === el?.id)
                      //   ]?.initialAttachments ?? [],
                      // )}
                      defaultFiles={FileItem.createManyFrom(damageLocal.attachments)}
                      disabled={!isUpdateEnabled}
                      onChange={(newFiles) => {
                        setDamageLocal((prev) => ({
                          ...prev,
                          attachments:
                            FileItem.toManyGeneralAttachmentInputDtoOrDto(newFiles) || undefined,
                          // initialAttachments:
                          //   FileItem.toManyGeneralAttachmentDto(newFiles) || undefined,
                        }));
                      }}
                      onUploadStarted={() => {
                        setIsAttachmentsUploading(true);
                      }}
                      onUploadFinished={() => {
                        setIsAttachmentsUploading(false);
                      }}
                      onValidationStatusChange={(filesValidationStatus) => {
                        if (filesValidationStatus)
                          setIsAttachmentsHaveErrors(
                            Object.values(filesValidationStatus).some((x) => x === false),
                          );
                      }}
                    />

                    {errors?.attachments && (
                      <FormHelperText error>
                        {ValidationHelper.getFormikErrorsAsString(errors?.attachments, {
                          isIncludeNested: true,
                        })}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              </Box>
            )}

            {/* Split item */}
            {isSplitActive && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <AppTypography variant='h3'>Split target damages</AppTypography>

                <Stack spacing={2}>
                  <Stack spacing={1}>
                    {ArrayHelper.isEmpty(damagesToSplitFromSource) && (
                      <NoDataAlert title='No target damages added' />
                    )}

                    {damagesToSplitFromSource?.map((newDamage, i) => {
                      const oldPoint =
                        Mapper.visualModel.mapGenericDamageToVisualModelPoint(damageLocal);
                      const newPoint =
                        Mapper.visualModel.mapGenericDamageToVisualModelPoint(newDamage);

                      const updateNewDamage = (newValue: typeof newDamage) => {
                        setDamagesToSplitFromSource(
                          damagesToSplitFromSource?.map((x) =>
                            x.id === newValue.id ? newValue : x,
                          ),
                        );
                      };

                      const removeNewDamage = (newValue: typeof newDamage) => {
                        setDamagesToSplitFromSource(
                          damagesToSplitFromSource?.filter((x) => x.id !== newValue.id),
                        );
                      };

                      return (
                        <AppCard key={i}>
                          <AppCardContent>
                            <Stack spacing={2}>
                              <Box
                                sx={{
                                  display: "grid",
                                  gap: 1,
                                  gridTemplateColumns: "0fr 1fr 1fr 0fr",
                                }}
                              >
                                <AppTypography variant='h4' sx={{ alignSelf: "center" }}>
                                  #{i + 1}
                                </AppTypography>

                                <FormControl margin='none' fullWidth>
                                  <VehiclePartTypeAutocompleteV2
                                    entityId={newDamage.partTypeId}
                                    isPreload={isPreload}
                                    isAutoSelectSingleOption
                                    size='small'
                                    required
                                    disabled={isPartTypeDisabled}
                                    metadata={{
                                      area: newDamage?.area,
                                      projection: newDamage?.projection,
                                    }}
                                    searchFilters={{
                                      area: newDamage?.area,
                                      projection: newDamage?.projection,
                                      category: newDamage?.partType?.category,
                                      type: newDamage?.partType?.type || undefined,
                                      descriptor: newDamage?.partType?.descriptor || undefined,
                                      isEnabledForDamageDetection: true,
                                    }}
                                    onChange={async (newValue) => {
                                      updateNewDamage({
                                        ...newDamage,
                                        partTypeId: newValue?.id,
                                        partType: newValue,
                                      });
                                    }}
                                    onPaginatedLoaded={(paginated) => {}}
                                  />
                                </FormControl>

                                <FormControl margin='none' fullWidth>
                                  <DamageTypeAutocompleteV2
                                    entityId={newDamage.damageTypeId}
                                    isPreload={isPreload}
                                    isAutoSelectSingleOption
                                    size='small'
                                    required
                                    disabled={!newDamage.partTypeId || isDamageTypeDisabled}
                                    searchFilters={{
                                      vehiclePartTypeId: newDamage.partTypeId || undefined,
                                    }}
                                    onChange={async (newValue) => {
                                      updateNewDamage({
                                        ...newDamage,
                                        damageTypeId: newValue?.id,
                                        damageType: newValue,
                                      });
                                    }}
                                  />
                                </FormControl>

                                <Stack direction='row'>
                                  <Tooltip title='Remove'>
                                    <AppIconButton
                                      onClick={(e) => {
                                        removeNewDamage(newDamage);
                                      }}
                                    >
                                      <AppIcon of='delete' />
                                    </AppIconButton>
                                  </Tooltip>
                                </Stack>
                              </Box>

                              <Box
                                sx={{
                                  display: "grid",
                                  gap: 1,
                                  gridTemplateColumns: "1fr 1fr",
                                }}
                              >
                                <Stack spacing={1}>
                                  <AppTypography variant='subtitle1'>Old position</AppTypography>

                                  <VisualModelSvgViewer
                                    visualModelId={undefined}
                                    visualModel={visualModel}
                                    imageSelector={{
                                      area: oldPoint?.meta.area || VehicleArea.Exterior,
                                      projection:
                                        oldPoint?.meta.projection || VehicleProjection.Front,
                                    }}
                                    points={oldPoint ? [oldPoint] : []}
                                    svgViewerProps={{
                                      isZoomEnabled: false,
                                      isResizeEnabled: false,
                                      isPointDragEnabled: false,
                                      svgProps: {
                                        width: 380,
                                        height: 340,
                                        minWidth: 300,
                                        minHeight: 300,
                                        maxWidth: 380,
                                        maxHeight: 340,
                                        cursor: "default",
                                        isDisableHoverEffects: false,
                                        point: {
                                          cursor: "default",
                                        },
                                      },
                                      defaultPointStyle: {
                                        ...VisualModelHelper.defaultSvgPointStyleForDamage,
                                      },
                                    }}
                                  />
                                </Stack>

                                <Stack spacing={1}>
                                  <AppTypography variant='subtitle1'>New position</AppTypography>

                                  <VisualModelSvgViewer
                                    visualModelId={undefined}
                                    visualModel={visualModel}
                                    imageSelector={{
                                      area: newPoint?.meta.area || VehicleArea.Exterior,
                                      projection:
                                        newPoint?.meta.projection || VehicleProjection.Front,
                                    }}
                                    points={newPoint ? [newPoint] : []}
                                    svgViewerProps={{
                                      isZoomEnabled: false,
                                      isResizeEnabled: false,
                                      isPointDragEnabled: true,
                                      svgProps: {
                                        width: 380,
                                        height: 340,
                                        minWidth: 300,
                                        minHeight: 300,
                                        maxWidth: 380,
                                        maxHeight: 340,
                                        cursor: "pointer",
                                        isDisableHoverEffects: false,
                                        point: {
                                          cursor: "pointer",
                                        },
                                      },
                                      defaultPointStyle: {
                                        ...VisualModelHelper.defaultSvgPointStyleForDamage,
                                      },
                                    }}
                                    onNewPointClicked={(params) => {
                                      updateNewDamage({
                                        ...newDamage,
                                        point: params.coords,
                                      });
                                    }}
                                    onPointMoved={(params) => {
                                      updateNewDamage({
                                        ...newDamage,
                                        point: params.endCoords,
                                      });
                                    }}
                                  />
                                </Stack>
                              </Box>

                              <AppTextArea
                                fullWidth
                                mode='notes'
                                margin='normal'
                                onChange={(ev) => {
                                  updateNewDamage({
                                    ...newDamage,
                                    notes: ev.target.value,
                                  });
                                }}
                                type='text'
                                value={newDamage.notes}
                                variant='outlined'
                              />

                              <FormControl fullWidth margin='dense'>
                                <FileUploader
                                  multiple
                                  defaultFiles={FileItem.createManyFrom(newDamage?.attachments)}
                                  onChange={(newFiles) => {
                                    updateNewDamage({
                                      ...newDamage,
                                      attachments:
                                        FileItem.toManyGeneralAttachmentInputDtoOrDto(newFiles) ||
                                        undefined,
                                    });
                                  }}
                                  onUploadStarted={() => {
                                    setIsAttachmentsUploading(true);
                                  }}
                                  onUploadFinished={() => {
                                    setIsAttachmentsUploading(false);
                                  }}
                                  onValidationStatusChange={(filesValidationStatus) => {
                                    if (filesValidationStatus)
                                      setIsAttachmentsHaveErrors(
                                        Object.values(filesValidationStatus).some(
                                          (x) => x === false,
                                        ),
                                      );
                                  }}
                                />
                              </FormControl>
                            </Stack>
                          </AppCardContent>
                        </AppCard>
                      );
                    })}

                    <Stack direction='row' sx={{ justifyContent: "flex-end" }}>
                      <AppButton
                        variant='outlined'
                        color='text'
                        size='small'
                        startIcon={<AppIcon of='add' />}
                        onClick={() => {
                          setDamagesToSplitFromSource([
                            ...(damagesToSplitFromSource || []),
                            createNewSplitTargetDamage(),
                          ]);
                        }}
                      >
                        Add target damage
                      </AppButton>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>
      }
    />
  );
}
